import type { CSSProperties, ReactNode } from "react";
import type { BlotterColumn } from "@/components/Blotter";
import CampaignStatusChip from "@/components/campaigns/CampaignStatusChip";
import { COLUMN_LABELS } from "@/constants/sendQueueCampaigns";
import {
  SEND_QUEUE_SORT_KEYS,
  type SendQueueItem,
  type SendQueueSortKey,
} from "@/interfaces/sendQueueCampaigns";
import ScheduledCell from "./ScheduledCell";
import SendTypeCell from "./SendTypeCell";

/**
 * The seven column definitions the Send Queue Blotter renders (GFG-194, unit U9). Mirrors the
 * shape of adminContactsColumns.tsx: shared TD/MUTED-style constants restated per column
 * (`cellStyle` REPLACES the Blotter's own `td`, Blotter.tsx:397), an `orDash`-style empty
 * convention, and a `buildColumns(deps)` entry point. Unlike the Admin Contacts table, the Send
 * Queue table has no per-user column visibility preferences (no `ColumnPrefs`/`applyPrefs` in
 * this ticket's interfaces), so there's no `buildAllColumns` + `applyPrefs` split -- `buildColumns`
 * just returns all seven, in the fixed order the ticket specifies.
 */

/** Hook-derived values the cells close over, resolved by the panel (Blotter.tsx:60-66). */
export interface SendQueueColumnDeps {
  /** Asks the panel to navigate to the campaign; the panel owns the navigation. */
  onOpenCampaign: (item: SendQueueItem) => void;
  /** Renders whatever row-action control the panel wants for this row (e.g. a row menu). */
  renderActions: (item: SendQueueItem) => ReactNode;
}

/**
 * Every column the API can sort by. Re-exports `SEND_QUEUE_SORT_KEYS` (interfaces/sendQueueCampaigns.ts)
 * rather than a second literal list, so this can never drift from the API's own whitelist --
 * `send_type` and `actions` are deliberately absent (not in the backend's sort whitelist).
 */
export const SORTABLE_COLUMN_IDS: readonly SendQueueSortKey[] = SEND_QUEUE_SORT_KEYS;

const DASH = "—";

/**
 * Base cell style. `cellStyle` **replaces** the Blotter's own `td` rather than merging with it
 * (Blotter.tsx:397), so a column needing one extra property has to restate the rest.
 */
const TD: CSSProperties = {
  padding: "14px 16px",
  fontSize: 14,
  color: "var(--color-rodeo-black)",
  borderBottom: "1px solid var(--color-smoke)",
  whiteSpace: "nowrap",
};

/**
 * Recipients is CENTRED, heading included — the pair below, never one alone. The column shipped
 * with a right-aligned cell under the Blotter's left-aligned heading, which put the number at the
 * far edge of a wide column, detached from the word naming it; that is the misalignment review
 * raised. Whatever alignment this column takes, `NUMERIC_TD` and `CENTER_TH` have to agree, which
 * is what `headerStyle` (Blotter.tsx:115) exists for.
 *
 * `tabular-nums` is independent of alignment and stays: it fixes every digit to the same advance
 * width, so 300 / 47 / 2 keep a common axis instead of jittering with the glyph widths.
 */
const NUMERIC_TD: CSSProperties = {
  ...TD,
  textAlign: "center",
  fontVariantNumeric: "tabular-nums",
};

/** The heading half of the pair above — centred over its centred column. */
const CENTER_TH: CSSProperties = {
  textAlign: "center",
};

/** Reset to plain text: the Campaign cell is a real `<button>` (it navigates), styled to read
 *  like the bold text a plain span would show elsewhere in this table. */
const CAMPAIGN_BUTTON_STYLE: CSSProperties = {
  fontFamily: "inherit",
  fontSize: "inherit",
  fontWeight: 600,
  color: "var(--color-rodeo-black)",
  background: "none",
  border: "none",
  padding: 0,
  margin: 0,
  cursor: "pointer",
  textAlign: "left",
};

/** Screen half of the empty convention: an organization-less campaign (b6d24d0) shows a dash. */
function orDash(value: string): string {
  return value.trim() ? value : DASH;
}

/**
 * The columns to hand the Blotter, in the ticket's exact order. A function (not a module
 * constant) because Campaign and Actions need the panel's `onOpenCampaign`/`renderActions` --
 * both are plain renderers closing over hook-derived values resolved by the panel, never hooks
 * themselves (Blotter.tsx:60-66).
 */
export function buildColumns(deps: SendQueueColumnDeps): BlotterColumn<SendQueueItem>[] {
  return [
    {
      key: "campaign",
      label: COLUMN_LABELS.campaign,
      sortable: true,
      sortValue: (r) => r.campaignTitle,
      exportValue: (r) => r.campaignTitle,
      cellStyle: TD,
      render: (r) => (
        <button type="button" style={CAMPAIGN_BUTTON_STYLE} onClick={() => deps.onOpenCampaign(r)}>
          {r.campaignTitle}
        </button>
      ),
    },
    {
      key: "organization",
      label: COLUMN_LABELS.organization,
      sortable: true,
      sortValue: (r) => r.organization,
      exportValue: (r) => r.organization,
      cellStyle: TD,
      render: (r) => orDash(r.organization),
    },
    {
      key: "send_type",
      label: COLUMN_LABELS.send_type,
      sortable: false,
      exportValue: (r) => r.sendLabel,
      cellStyle: TD,
      render: (r) => <SendTypeCell item={r} />,
    },
    {
      key: "recipients",
      label: COLUMN_LABELS.recipients,
      sortable: true,
      sortValue: (r) => r.recipients,
      exportValue: (r) => String(r.recipients),
      cellStyle: NUMERIC_TD,
      headerStyle: CENTER_TH,
      render: (r) => r.recipients.toLocaleString(),
    },
    {
      key: "scheduled_at",
      label: COLUMN_LABELS.scheduled_at,
      sortable: true,
      sortValue: (r) => r.scheduledAt,
      exportValue: (r) => r.scheduledAt,
      cellStyle: TD,
      render: (r) => <ScheduledCell item={r} />,
    },
    {
      key: "status",
      label: COLUMN_LABELS.status,
      sortable: true,
      sortValue: (r) => r.status,
      exportValue: (r) => r.status,
      cellStyle: TD,
      render: (r) => <CampaignStatusChip status={r.status} />,
    },
    {
      key: "actions",
      label: COLUMN_LABELS.actions,
      sortable: false,
      cellStyle: TD,
      render: (r) => deps.renderActions(r),
    },
  ];
}
